import { Injectable, NgZone, inject, signal } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { ExamEventTrackerService } from '../services/exam-event-tracker.service';

const CHANNEL_NAME = 'mock-test-exam-tabs';
const PROBE_TIMEOUT_MS = 300;

// crypto.randomUUID only exists in secure contexts (https / localhost).
const createId = (): string =>
  typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;

type TabMessage =
  | { type: 'PING'; from: string; requestId: string }
  | { type: 'PONG'; from: string; to: string; requestId: string; examActive: boolean; claimedAt: number }
  | { type: 'EXAM_CLAIMED'; from: string; claimedAt: number }
  | { type: 'EXAM_RELEASED'; from: string };

export interface TabProbeResult {
  /** Number of other open tabs/windows of this app on the same origin. */
  otherTabCount: number;
  /** true when one of those tabs is currently running an exam. */
  examActiveElsewhere: boolean;
}

/**
 * Cross-tab coordination for the exam flow, built on BroadcastChannel (same
 * origin, same browser profile). Every tab of the app answers PINGs, which
 * lets a tab count its siblings before an exam starts and lets any tab opened
 * while an exam is running (new tab, "Duplicate tab", copied URL) find out
 * and block itself. Like the rest of the security layer this is a deterrent,
 * not a boundary: a different browser/profile/device is invisible to it.
 */
@Injectable({ providedIn: 'root' })
export class ExamTabLockService {
  private readonly zone = inject(NgZone);
  private readonly toastr = inject(ToastrService);
  private readonly eventTracker = inject(ExamEventTrackerService);

  // Deliberately in-memory, not sessionStorage: browsers copy sessionStorage
  // into a duplicated tab, which would give both tabs the same id.
  private readonly tabId = createId();
  private readonly channel: BroadcastChannel | null =
    typeof BroadcastChannel === 'undefined' ? null : new BroadcastChannel(CHANNEL_NAME);
  private readonly pendingProbes = new Map<string, Map<string, boolean>>();
  private examClaimedAt = 0;
  private readonly otherExamTabs = new Set<string>();
  private readonly reportedDuplicateTabs = new Set<string>();

  private readonly blockedState = signal(false);
  /** true while another tab owns the running exam; this tab must not be used. */
  readonly isBlockedByOtherTab = this.blockedState.asReadonly();

  private readonly examRunningState = signal(false);
  /** true while this tab is running an exam (between claimExam and releaseExam). */
  readonly isExamRunningHere = this.examRunningState.asReadonly();

  constructor() {
    if (!this.channel) {
      return;
    }

    this.channel.onmessage = (event: MessageEvent<TabMessage>) => this.zone.run(() => this.onMessage(event.data));
    window.addEventListener('pagehide', () => this.releaseExam());

    // Find out straight away if this tab was opened while an exam is running.
    void this.probe();
  }

  /** Asks every other tab of the app to identify itself. */
  probe(): Promise<TabProbeResult> {
    if (!this.channel) {
      return Promise.resolve({ otherTabCount: 0, examActiveElsewhere: false });
    }

    const requestId = createId();
    const responses = new Map<string, boolean>();
    this.pendingProbes.set(requestId, responses);
    this.post({ type: 'PING', from: this.tabId, requestId });

    return new Promise((resolve) => {
      setTimeout(() => {
        this.pendingProbes.delete(requestId);
        resolve({
          otherTabCount: responses.size,
          examActiveElsewhere: Array.from(responses.values()).some(Boolean),
        });
      }, PROBE_TIMEOUT_MS);
    });
  }

  /** Marks this tab as the one running the exam and blocks every other tab. */
  claimExam(): void {
    if (this.examClaimedAt) {
      return;
    }

    this.examClaimedAt = Date.now();
    this.examRunningState.set(true);
    this.post({ type: 'EXAM_CLAIMED', from: this.tabId, claimedAt: this.examClaimedAt });
  }

  releaseExam(): void {
    if (!this.examClaimedAt) {
      return;
    }

    this.examClaimedAt = 0;
    this.examRunningState.set(false);
    this.reportedDuplicateTabs.clear();
    this.post({ type: 'EXAM_RELEASED', from: this.tabId });
  }

  private onMessage(message: TabMessage): void {
    if (message.from === this.tabId) {
      return;
    }

    switch (message.type) {
      case 'PING':
        this.post({
          type: 'PONG',
          from: this.tabId,
          to: message.from,
          requestId: message.requestId,
          examActive: this.examClaimedAt > 0,
          claimedAt: this.examClaimedAt,
        });
        if (this.examClaimedAt) {
          this.onDuplicateTabOpened(message.from);
        }
        break;

      case 'PONG':
        if (message.to !== this.tabId) {
          return;
        }
        this.pendingProbes.get(message.requestId)?.set(message.from, message.examActive);
        if (message.examActive) {
          this.markOtherTabExamActive(message.from, message.claimedAt);
        }
        break;

      case 'EXAM_CLAIMED':
        this.markOtherTabExamActive(message.from, message.claimedAt);
        break;

      case 'EXAM_RELEASED':
        this.otherExamTabs.delete(message.from);
        this.blockedState.set(this.otherExamTabs.size > 0);
        break;
    }
  }

  private markOtherTabExamActive(otherTabId: string, otherClaimedAt: number): void {
    // Two tabs racing past the pre-start checks: the earlier claim keeps the
    // exam (tab id breaks an exact tie) and the later one gets blocked.
    if (this.examClaimedAt) {
      const thisTabWins =
        this.examClaimedAt < otherClaimedAt || (this.examClaimedAt === otherClaimedAt && this.tabId < otherTabId);
      if (thisTabWins) {
        return;
      }
    }

    this.otherExamTabs.add(otherTabId);
    this.blockedState.set(true);
  }

  private onDuplicateTabOpened(otherTabId: string): void {
    // A new tab probes more than once (on load and in the route guard).
    if (this.reportedDuplicateTabs.has(otherTabId)) {
      return;
    }

    this.reportedDuplicateTabs.add(otherTabId);
    this.toastr.warning(
      'Another tab of this test was opened and has been blocked. Continue your test in this tab only.',
      'Duplicate tab detected',
    );
    this.eventTracker.track('MULTIPLE_EXAM_SESSIONS_DETECTED');
  }

  private post(message: TabMessage): void {
    this.channel?.postMessage(message);
  }
}
